import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { MapPin, Waves, ShieldCheck, Sparkles } from "lucide-react";
import { api } from "@/lib/apiClient";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { spotPath, beginnerPath, countryPath } from "@/lib/seo";

export default function RegionHub({ topic }) {
  const { country, region } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const endpoint =
    topic === "beginner-surf"
      ? `/seo/beginner-surf/${country}/${region}`
      : `/seo/regions/${country}/${region}`;

  useEffect(() => {
    setErr("");
    api
      .get(endpoint)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.status === 404 ? "not-found" : "error"));
  }, [endpoint]);

  useEffect(() => {
    if (err === "not-found")
      navigate(`/surf-spots/${country}`, { replace: true });
  }, [err, navigate, country]);

  if (!data) {
    return <div className="max-w-5xl mx-auto p-8 text-muted-foreground">Loading…</div>;
  }

  const isBeginner = topic === "beginner-surf";
  const path = isBeginner
    ? beginnerPath(data.country, data.region)
    : `/surf-spots/${data.country_slug}/${data.slug}`;

  const heading = isBeginner
    ? `Beginner-friendly surf in ${data.region}`
    : `Surf spots in ${data.region}, ${data.country}`;

  const description = isBeginner
    ? `${data.spot_count} beginner-friendly surf spots in ${data.region}, ${data.country}. Manageable waves, safer beach types, live conditions and community reports.`
    : `Every published surf spot in ${data.region}, ${data.country} — ${data.spot_count} in total${
        data.beginner_count ? ` (${data.beginner_count} beginner-friendly)` : ""
      }. Live forecasts, safety context and community confidence.`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: heading,
    numberOfItems: data.spot_count,
    itemListElement: data.spots.slice(0, 20).map((s, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url:
        (typeof window !== "undefined" ? window.location.origin : "") +
        spotPath(s),
      name: s.name,
    })),
  };

  return (
    <div className="max-w-5xl mx-auto p-6 md:p-8" data-testid={isBeginner ? "beginner-hub" : "region-hub"}>
      <Seo title={heading} description={description} path={path} jsonLd={jsonLd} />
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Surf spots", to: "/surf-spots" },
          { label: data.country, to: countryPath(data.country) },
          { label: isBeginner ? `${data.region} · Beginner` : data.region },
        ]}
      />

      <header className="mb-8 flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-primary/10 grid place-items-center text-primary shrink-0">
          {isBeginner ? <ShieldCheck className="w-6 h-6" /> : <Waves className="w-6 h-6" />}
        </div>
        <div>
          <h1
            className="text-3xl md:text-5xl font-semibold tracking-tight"
            data-testid={isBeginner ? "beginner-heading" : "region-heading"}
          >
            {heading}
          </h1>
          <p className="text-muted-foreground mt-3 max-w-2xl text-base md:text-lg">
            {description}
          </p>
          {!isBeginner && data.beginner_count > 0 ? (
            <Link
              to={beginnerPath(data.country, data.region)}
              className="inline-flex items-center gap-1 mt-3 text-sm text-primary hover:underline"
              data-testid="beginner-cta"
            >
              <Sparkles className="w-3.5 h-3.5" />
              See only beginner-friendly spots
            </Link>
          ) : null}
        </div>
      </header>

      <section>
        <h2 className="text-lg md:text-xl font-medium mb-4">
          {data.spot_count} {data.spot_count === 1 ? "spot" : "spots"}
        </h2>
        <div className="grid sm:grid-cols-2 gap-4">
          {data.spots.map((s) => (
            <Link
              key={s.id}
              to={spotPath(s)}
              data-testid={`spot-card-${s.slug}`}
              className="group rounded-xl border bg-card hover:bg-accent transition-colors p-4"
            >
              <div className="flex items-center gap-2">
                <div className="font-medium group-hover:underline underline-offset-4">
                  {s.name}
                </div>
                {s.beginner_suitable ? (
                  <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                    Beginner
                  </span>
                ) : null}
              </div>
              <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {s.nearest_town || s.region}
                {s.break_type ? <span className="ml-2">• {s.break_type} break</span> : null}
              </div>
              {s.short_description ? (
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                  {s.short_description}
                </p>
              ) : null}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
